import { useRef } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import SiteFooter from '../components/SiteFooter';
import Wiki from '../components/Wiki';
import useReveal, { Scrubbed } from '../useReveal';

const P = process.env.PUBLIC_URL;

const WORLDS = [
  { name: 'Ice', vibe: 'Crisp blue snowfields, snowballs and ice spikes.', coin: 5, span: 'col-2', grad: 'linear-gradient(140deg, #8fbdf2, #2f4f7a)' },
  { name: 'Beach', vibe: 'Warm sand, swaying palms and beach umbrellas.', coin: 9, span: 'col-2', grad: 'linear-gradient(140deg, #ffd98a, #df8f43)' },
  { name: 'Lava', vibe: 'Scorched rock, glowing magma and fire pillars.', coin: 14, span: 'col-2', grad: 'linear-gradient(140deg, #ff8a5c, #7a1f1f)' },
  { name: 'Night', vibe: 'Moonlit crystals and softly glowing mushrooms.', coin: 18, span: 'col-3', grad: 'linear-gradient(140deg, #6273e2, #191d46)' },
  { name: 'Space', vibe: 'Asteroids, energy barriers and distant planets.', coin: 23, span: 'col-3', grad: 'linear-gradient(140deg, #8d66e8, #160f3a)' },
];

const STEPS = [
  { t: 'Steer', d: 'Drag left or right to carve across the slope. On desktop, the arrow keys work too.' },
  { t: 'Hop', d: 'Tap, or flick up while sliding, to clear low hurdles and snatch the floating coin arcs.' },
  { t: 'Boost', d: 'Hold a turn to charge your meter, then fire BOOST for a burst of pure speed.' },
];

const POWERS = ['Shield', 'Magnet', 'Nuke', 'Score Star', 'Ghost', 'Extra Life', 'Nitro', '2x Surge', 'Phantom', 'Smash'];

const card = { background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r)', padding: 'clamp(1.5rem, 2.5vw, 2.2rem)' };

export default function SlopeSliders() {
  const scope = useRef(null);
  useReveal(scope);

  return (
    <div className="theme-ice grain" ref={scope}>
      <div className="site">
        <Nav
          items={[
            { label: 'Pot Belly Pig Studios', to: '/' },
            { label: 'Slope Sliders', to: '/slope-sliders', active: true },
            { label: 'Player Guide', href: '#wiki' },
          ]}
        />

        {/* ATTENTION — artistic asymmetry */}
        <section className="hero hero-split container">
          <div>
            <span className="eyebrow">Endless icy runner</span>
            <h1 className="h1 display-accent">An endless icy descent.</h1>
            <p className="lede">
              Guide a determined little penguin down an infinite, ever-steepening mountain. Dodge
              everything, scoop up coins, chain drifts into boosts, and chase a brand-new best run.
            </p>
            <div className="cta-row">
              <a href="#play" className="btn btn-primary">How to play</a>
              <a href="#wiki" className="btn btn-ghost">Open the guide</a>
            </div>
          </div>
          <div style={{ position: 'relative', display: 'grid', placeItems: 'center' }}>
            <div
              className="hero-glow"
              style={{ background: 'radial-gradient(42% 42% at 50% 50%, rgba(58,168,255,0.45), transparent 70%)' }}
            />
            <img
              src={`${P}/slope-sliders-icon.png`}
              alt="Slope Sliders"
              className="pig-float"
              style={{ width: 'clamp(220px, 34vw, 440px)', borderRadius: '22%', filter: 'drop-shadow(0 34px 60px rgba(30,60,120,0.4))' }}
            />
          </div>
        </section>

        {/* INTEREST — worlds bento */}
        <section className="section container" id="worlds">
          <div className="section-head" data-reveal>
            <h2 className="h2 display-accent">Five worlds, one slope.</h2>
            <p className="section-lede lede">
              The mountain cycles through five biomes, each with its own look, music and hazards.
              Push deeper and the coins are worth far more &mdash; then the loop repeats, a little harder.
            </p>
          </div>

          <div className="bento bento-worlds" style={{ marginTop: 'clamp(2.5rem,5vw,4rem)' }}>
            {WORLDS.map((w) => (
              <div className={`cell ${w.span}`} key={w.name} data-reveal>
                <div className="cell-media" data-img>
                  <div className="cell-bg" style={{ background: w.grad }} />
                </div>
                <div className="cell-scrim" />
                <span
                  style={{
                    position: 'absolute', top: '1.1rem', right: '1.1rem', zIndex: 2,
                    padding: '0.35rem 0.7rem', borderRadius: 999, fontWeight: 700, fontSize: '0.82rem',
                    background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff',
                  }}
                >
                  {w.coin} coins
                </span>
                <span className="cell-title display-accent" style={{ color: '#fff', fontSize: 'clamp(1.6rem,3vw,2.4rem)' }}>{w.name}</span>
                <span className="cell-sub" style={{ color: 'rgba(255,255,255,0.88)' }}>{w.vibe}</span>
              </div>
            ))}
          </div>
        </section>

        {/* INTEREST — how to play */}
        <section className="section container" id="play">
          <div className="section-head" data-reveal>
            <h2 className="h2">The whole game runs on one thumb.</h2>
            <p className="section-lede lede">Three moves to master. The rest is nerve.</p>
          </div>
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginTop: 'clamp(2rem,4vw,3rem)' }}
          >
            {STEPS.map((s) => (
              <div style={card} key={s.t} data-reveal>
                <h3 className="display-accent" style={{ fontSize: '1.5rem', marginBottom: '0.6rem' }}>{s.t}</h3>
                <p style={{ color: 'var(--muted)' }}>{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* DESIRE — scrub + power-up marquee */}
        <section className="section container">
          <p className="statement scrub display-accent" data-reveal style={{ maxWidth: '26ch' }}>
            <Scrubbed text="Slide. Dodge. Boost. Crash. Spend. Go further." />
          </p>
        </section>
        <section className="section" style={{ paddingBlock: 'clamp(2.5rem,6vw,5rem)' }}>
          <div className="marquee">
            <div className="marquee-track">
              {[...POWERS, ...POWERS].map((p, i) => (
                <span className="marquee-item" key={i}>{p}</span>
              ))}
            </div>
          </div>
        </section>

        {/* DESIRE/INTEREST — full player guide */}
        <section className="section container" id="wiki">
          <div className="section-head" data-reveal style={{ maxWidth: '40ch' }}>
            <h2 className="h2">The complete guide.</h2>
            <p className="section-lede lede">
              Worlds, power-ups, active items, upgrades, sleds, currencies and strategy &mdash; the full
              player guide, straight from the game. Or{' '}
              <a href={`${P}/slope-sliders-wiki.md`} style={{ color: 'var(--accent)', textDecoration: 'underline' }}>open the raw source</a>.
            </p>
          </div>
          <div className="guide-doc" data-reveal style={{ marginTop: 'clamp(2rem,4vw,3rem)' }}>
            <Wiki />
          </div>
        </section>

        {/* ACTION */}
        <SiteFooter ctaTitle="Think you can go the distance?" ctaButton="Read the guide" ctaHref="#wiki" />
      </div>
    </div>
  );
}
